"use client";
import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { Home, Search, Package } from "lucide-react";
import EmptyState from "@/app/components/elements/EmptyState";
import useNotFound from "@/app/lib/features/notFound/notFound.hook";
import ProductDetailSkeleton from "@/app/components/elements/skeletons/ProductDetailSkeleton";
import { LucideIcon } from "lucide-react";
import { trackGeneric } from "@/app/lib/tracking/interactions";

interface NotFoundPageProps {
  /** notFound feature yerine doğrudan içerik vermek için */
  title?: string;
  description?: string;
  icon?: LucideIcon;
  primaryLabel?: string;
  primaryHref?: string;
  showHomeButton?: boolean;
}

const NotFoundPage = ({
  title,
  description,
  icon,
  primaryLabel,
  primaryHref,
  showHomeButton,
}: NotFoundPageProps) => {
  const router = useRouter();
  const { notFoundData, isLoading } = useNotFound();

  // Inline 404 (ürün/kampanya/kategori bulunamadı) görüntülenmesi — route-level
  // not-found.tsx'ten farklı bir sinyal (kırık ürün/kampanya linkleri).
  useEffect(() => {
    trackGeneric("not_found_inline", "view");
  }, []);

  // Doğrudan prop geçildiyse API'ye gerek yok
  if (title) {
    const actions = [];
    if (primaryLabel && primaryHref) {
      actions.push({ label: primaryLabel, href: primaryHref });
    }
    if (showHomeButton) {
      actions.push({
        label: "Anasayfa",
        href: "/",
        variant: "outline" as const,
      });
    }

    return (
      <div className="container-main min-h-[50vh] flex items-center justify-center">
        <EmptyState
          icon={icon ?? Search}
          title={title}
          description={description}
          actions={actions}
        />
      </div>
    );
  }

  if (isLoading) {
    return <ProductDetailSkeleton />;
  }

  if (!notFoundData) {
    return (
      <div className="container-main min-h-[50vh] flex items-center justify-center">
        <EmptyState
          icon={Home}
          title="Sayfa Bulunamadı"
          description="Aradığınız sayfa mevcut değil."
          actions={[{ label: "Anasayfa", href: "/" }]}
        />
      </div>
    );
  }

  const typeMap: Record<
    string,
    { title: string; description: string; icon: LucideIcon }
  > = {
    product: {
      title: "Ürün Bulunamadı",
      description: "Aradığınız ürün mevcut değil veya kaldırılmış olabilir.",
      icon: Package,
    },
    category: {
      title: "Kategori Bulunamadı",
      description: "Aradığınız kategori mevcut değil.",
      icon: Search,
    },
    brand: {
      title: "Marka Bulunamadı",
      description: "Aradığınız marka sayfası mevcut değil.",
      icon: Search,
    },
  };

  const matched = typeMap[notFoundData.type] ?? {
    title: "Sayfa Bulunamadı",
    description: "Aradığınız sayfa mevcut değil.",
    icon: Home,
  };

  const actions = [
    {
      label: notFoundData.button_text,
      onClick: () => router.push(notFoundData.slug),
    },
    ...(notFoundData.type !== "page"
      ? [{ label: "Anasayfa", href: "/", variant: "outline" as const }]
      : []),
  ];

  return (
    <div className="container-main min-h-[50vh] flex items-center justify-center">
      <EmptyState
        icon={matched.icon}
        title={matched.title}
        description={matched.description}
        actions={actions}
      />
    </div>
  );
};

export default NotFoundPage;
